import type { BaseValidation } from '@/components/datamodel/types';
import type { PersistedItem } from '@/types/DataModel';

/** Formats of the wireframe list until tenants configure their own (LAX-10541, D-046); ZIP follows with LAX-10539. */
const DAM_UPLOAD_EXTENSIONS = ['jpg', 'jpeg', 'png', 'tif', 'tiff', 'doc', 'docx', 'pdf'];

/** Labels of the supported formats as the wireframe lists them. */
const DAM_UPLOAD_FORMAT_LABELS = 'JPG, PNG, TIFF, DOC, PDF';

type DamUploadStatus = 'uploading' | 'success' | 'error';

/** `format`: not a supported file type. `failed`: staging failed. `duplicate` / `create`: rejected by "Create". */
type DamUploadError = 'format' | 'failed' | 'duplicate' | 'create';

interface DamUploadFile {
    id: string;
    file: File;
    status: DamUploadStatus;
    progress: number;
    startedAt?: number;
    error?: DamUploadError;
    /** Staged upload id once `POST /uploads` answered. */
    uploadId?: number;
    /** Pixel size of images, read in the browser. */
    dimensions?: { width: number; height: number };
    /** Products to link when the asset is created (optional, FR-LINK-01). */
    items: PersistedItem[];
    /** Business values of the `asset_upload` attributes. */
    values: Record<string, unknown>;
    /** Result of the validation without storing (D-051) for the current `values`; `undefined` until it arrived. */
    validation?: DamUploadValidation;
}

/** Validation messages of one file per attribute (`''` = the file as a whole). */
interface DamUploadValidation {
    /** The validated `values` object; a result for older values is not applied. */
    values: Record<string, unknown>;
    /** Attributes reported as missing (rule `is_mandatory`), counted as "Incomplete asset data". */
    missing: string[];
    errors: Record<string, BaseValidation[]>;
    warnings: Record<string, BaseValidation[]>;
}

const MANDATORY_RULE = 'is_mandatory';

const toUploadValidation = (values: Record<string, unknown>, validations: Record<string, BaseValidation[]>): DamUploadValidation => {
    const result: DamUploadValidation = { values, missing: [], errors: {}, warnings: {} };
    for (const [attributeName, entries] of Object.entries(validations)) {
        for (const entry of entries ?? []) {
            if (entry.name === MANDATORY_RULE) {
                result.missing.push(attributeName);
            } else {
                (result[entry.level === 'Warning' ? 'warnings' : 'errors'][attributeName] ??= []).push(entry);
            }
        }
    }
    return result;
};

const extensionOf = (name: string) => {
    const dot = name.lastIndexOf('.');
    return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
};

const isSupportedUpload = (file: File) => DAM_UPLOAD_EXTENSIONS.includes(extensionOf(file.name));

/** Dropped folders deliver their system files too; those are skipped, not reported. */
const isHiddenUpload = (file: File) => {
    const path = (file as File & { path?: string }).path ?? file.name;
    return path.split('/').some((segment) => segment.startsWith('.') || segment === '__MACOSX');
};

const hasValue = (value: unknown) =>
    value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);

/** Staged files that can be created; failed rows, duplicates included, stay in step 1. */
const isUploaded = (file: DamUploadFile) => file.status === 'success' && file.uploadId !== undefined && !file.error;

/** Rows listed under "Failed uploads": staging failed, unsupported format, or rejected by "Create". */
const isFailed = (file: DamUploadFile) => Boolean(file.error);

/**
 * Uploaded files that miss a mandatory value: as reported by the validation (D-051), until it
 * arrived the mandatory `asset_upload` attributes without a value (D-046).
 */
const isIncomplete = (file: DamUploadFile, mandatory: string[]) =>
    (file.validation ? file.validation.missing.length > 0 : mandatory.some((name) => !hasValue(file.values[name])));

const hasValidationErrors = (file: DamUploadFile) => Object.keys(file.validation?.errors ?? {}).length > 0;

const hasValidationWarnings = (file: DamUploadFile) => Object.keys(file.validation?.warnings ?? {}).length > 0;

/** The common value of an attribute over several files, or `mixed` when they differ. */
const sharedValue = (files: DamUploadFile[], name: string): unknown => {
    const values = files.map((file) => file.values[name]);
    const first = JSON.stringify(values[0] ?? null);
    return values.every((value) => JSON.stringify(value ?? null) === first) ? values[0] : MIXED_VALUE;
};

const MIXED_VALUE = Symbol('mixed');

/** Products linked to any of the files, without duplicates. */
const unionItems = (files: DamUploadFile[]): PersistedItem[] => {
    const byKey = new Map<string, PersistedItem>();
    files.forEach((file) => file.items.forEach((item) => byKey.set(item.primaryKey__, item)));
    return [...byKey.values()];
};

export {
    DAM_UPLOAD_EXTENSIONS,
    DAM_UPLOAD_FORMAT_LABELS,
    extensionOf,
    hasValidationErrors,
    hasValidationWarnings,
    hasValue,
    isFailed,
    isHiddenUpload,
    isIncomplete,
    isSupportedUpload,
    isUploaded,
    MIXED_VALUE,
    sharedValue,
    toUploadValidation,
    unionItems,
};
export type { DamUploadError, DamUploadFile, DamUploadStatus, DamUploadValidation };
